import type { Lead } from '@up2stay/up2promo-api-client';
import { formatLeadDisplayName } from '@up2stay/up2promo-commons';
import { cn } from '@up2stay/up2promo-ui';
import {
  ArrowLeftRight,
  Calendar,
  FileText,
  Lock,
  Pencil,
  ScanLine,
} from 'lucide-react';

import type { LeadCardMetaLines } from '@/core/components/leads/lead-card-content';
import { getLeadCardLabelChips } from '@/core/components/leads/lead-card-label-chips';
import type { LeadCardLabelMaps } from '@/core/components/leads/lead-card-labels';
import { leadStatusAccent } from '@/core/components/leads/lead-status-colors';
import {
  formatLeadCapturedAt,
  leadCompanyLine,
  leadInitials,
  leadSourceVisual,
  personInitials,
} from '@/core/components/leads/leads-list-display';

type LeadBoardCardContentProps = {
  lead: Lead;
  locale: string;
  sourceLabels: Record<string, string>;
  meta?: LeadCardMetaLines | null;
  labelMaps?: LeadCardLabelMaps;
  /** Show primary status dot on name (mock: Neu). */
  showStatusDot?: boolean;
};

/** Compact Kanban card chrome matching LeadsBoardDesktop mock. */
export function LeadBoardCardContent({
  lead,
  locale,
  sourceLabels,
  meta,
  labelMaps,
  showStatusDot = lead.status === 'new',
}: LeadBoardCardContentProps) {
  const name =
    formatLeadDisplayName(lead.firstName, lead.lastName) || lead.email || '—';
  const company = leadCompanyLine(lead, false);
  const sourceLabel = sourceLabels[lead.source] ?? lead.source;
  const sourceVisual = leadSourceVisual(lead.source);
  const assigneeName = meta?.assigneeValue?.trim() || '';
  const captured = formatLeadCapturedAt(lead.createdAt, locale);
  const { events } = getLeadCardLabelChips(lead, labelMaps);
  const eventName = events[0];
  const isPrivate = meta?.visibility === 'private';

  return (
    <div className="flex flex-col gap-2 text-left">
      <div className="flex items-start gap-2">
        <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-lg bg-muted text-[10px] font-bold text-foreground">
          {leadInitials(lead)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[13px] font-semibold">
            {showStatusDot ? (
              <span
                className="size-1.5 shrink-0 rounded-full"
                style={{ backgroundColor: leadStatusAccent(lead.status) }}
                aria-hidden
              />
            ) : null}
            <span className="truncate text-[color:var(--ghost-accent-fg)]">
              {name}
            </span>
          </div>
          {company ? (
            <div className="truncate text-[11.5px] text-muted-foreground">
              {company}
            </div>
          ) : null}
        </div>
        {assigneeName ? (
          <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-[color:var(--ghost-accent-bg)] text-[8px] font-bold text-[color:var(--ghost-accent-fg)]">
            {personInitials(assigneeName)}
          </span>
        ) : null}
      </div>

      {eventName ? (
        <span className="inline-flex h-5 w-fit max-w-full items-center gap-1 rounded-md bg-muted px-1.5 text-[11px]">
          <Calendar className="size-[11px] shrink-0" aria-hidden />
          <span className="truncate">{eventName}</span>
        </span>
      ) : null}

      <div className="flex items-center justify-between gap-1.5 text-[11.5px] text-muted-foreground">
        <span className="inline-flex min-w-0 items-center gap-1">
          <SourceIcon kind={sourceVisual.icon} />
          <span className="truncate">{sourceLabel}</span>
        </span>
        <span className="inline-flex shrink-0 items-center gap-1.5">
          {isPrivate ? <Lock className="size-3" aria-hidden /> : null}
          {captured}
        </span>
      </div>
    </div>
  );
}

function SourceIcon({
  kind,
}: {
  kind: 'form' | 'scan' | 'exchange' | 'manual';
}) {
  const className = cn('size-3 shrink-0');
  switch (kind) {
    case 'form':
      return <FileText className={className} aria-hidden />;
    case 'scan':
      return <ScanLine className={className} aria-hidden />;
    case 'exchange':
      return <ArrowLeftRight className={className} aria-hidden />;
    default:
      return <Pencil className={className} aria-hidden />;
  }
}
